/* eslint-disable max-lines-per-function */
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import {
  Abteilung,
  Arbeitsbereich,
  Dienstplanuebernahme,
} from '../../entities';
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/table';
import { E2Ntable, E2NtableHeader, E2NtableRow } from '@/components/Table';
import { useDienstplanColumnDefs } from '../../columns/columns';
import { AlleUebernehmenDialog } from './AlleUebernehmenDialog';
import { AlleIgnorierenDialog } from './AlleIgnorierenDialog';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { NoDataFallback } from '@/domain/arbeitszeiten/shared/components/NoDataFallback';

export type ArbeitsbereichFilter = {
  arbeitsbereiche?: string[];
  abteilungen?: string[];
};

export type DienstplanuebernahmeTableRow = Dienstplanuebernahme & {
  loading: boolean;
};

interface DataTableProps<TData> {
  data: TData[];
  nameFilter: string;
  arbeitsbereichFilter: Arbeitsbereich[];
  abteilungFilter: Abteilung[];
}

export function DienstplanuebernahmeTable({
  data,
  nameFilter,
  arbeitsbereichFilter,
  abteilungFilter,
}: DataTableProps<Dienstplanuebernahme>) {
  const [globalLoading, setGlobalLoading] = useState(false);
  const [tableData, setTableData] = useState<DienstplanuebernahmeTableRow[]>(
    data.map((dienstplanuebernahme) => ({
      ...dienstplanuebernahme,
      loading: false,
    })),
  );

  useEffect(() => {
    setTableData(
      data.map((dienstplanuebernahme) => ({
        ...dienstplanuebernahme,
        loading: false,
      })),
    );
  }, [data]);
  const handleLoadingToggle = (id: string) => {
    setTableData((prev) =>
      prev.map((dienstplanuebernahme) =>
        dienstplanuebernahme.id === id
          ? { ...dienstplanuebernahme, loading: !dienstplanuebernahme.loading }
          : dienstplanuebernahme,
      ),
    );
    setGlobalLoading((prev) => !prev);
  };

  const columns: ColumnDef<DienstplanuebernahmeTableRow>[] =
    useDienstplanColumnDefs({
      switchLoadingStaste: handleLoadingToggle,
    });

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'mitarbeiter', value: nameFilter },
  ]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'arbeitszeit', desc: false },
  ]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: { columnFilters, sorting },
    onSortingChange: setSorting,
  });

  const { currentStandort } = useStandortContext();
  useEffect(() => {
    const mitarbeiterColumn = table.getColumn('mitarbeiter');
    const arbeitsbereichColumn = table.getColumn('arbeitsbereich');

    if (mitarbeiterColumn) {
      mitarbeiterColumn.setFilterValue(nameFilter);
    }

    if (arbeitsbereichColumn) {
      const filterValue = {
        arbeitsbereiche: arbeitsbereichFilter.map(
          (arbeitsbereich) => arbeitsbereich.id,
        ),
        abteilungen: currentStandort.abteilungenEnabled
          ? abteilungFilter.map((abteilung) => abteilung.id)
          : [],
      };
      arbeitsbereichColumn.setFilterValue(filterValue);
    }
  }, [
    nameFilter,
    arbeitsbereichFilter,
    abteilungFilter,
    currentStandort.abteilungenEnabled,
    table,
  ]);
  return (
    <div className="relative">
      <div className="absolute top-0 right-0 p-4 z-50">
        <div className="flex flex-row gap-1">
          <AlleUebernehmenDialog
            isLoading={globalLoading}
            setIsLoading={setGlobalLoading}
            dienstplanuebernahmen={table
              .getFilteredRowModel()
              .rows.map((row) => row.original)}
          />
          <AlleIgnorierenDialog
            isLoading={globalLoading}
            setIsLoading={setGlobalLoading}
            dienstplanuebernahmen={table
              .getFilteredRowModel()
              .rows.map((row) => row.original)}
          />
        </div>
      </div>
      <div className="overflow-x-scroll">
        <E2Ntable className="md:mt-0 mt-8">
          <E2NtableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow className="border-b-0" key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      className={
                        'px-0 text-xs normal-case font-semibold h-0 pt-4'
                      }
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </E2NtableHeader>
          <TableBody className="p-0">
            {table.getSortedRowModel().rows?.length ? (
              table.getSortedRowModel().rows.map((row) => {
                return (
                  <E2NtableRow
                    className={`hover:bg-transparent ${'[&_td]:bg-white'}`}
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}>
                    {row
                      .getVisibleCells()
                      .map((cell) =>
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        ),
                      )}
                  </E2NtableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center">
                  <NoDataFallback />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </E2Ntable>
      </div>
    </div>
  );
}
